import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Card, Alert } from 'react-bootstrap';
import { useAddSecurityGroupMutation, useGetOrganizationsQuery } from '../../services/portal';

/**
 * A form for creating a new security group within an organization.
 * Handles validation, submission via RTK Query, and navigation on success.
 * @returns {JSX.Element} New Security Group creation page.
 */
const CreateSecurityGroup = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    groupName: '',
    groupDescription: '',
    organizationId: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: organizations, isLoading: orgLoading, error: orgError } = useGetOrganizationsQuery({
    forceRefetch: true
  });
  const [addSecurityGroup] = useAddSecurityGroupMutation();

  /**
   * Updates formData when inputs change and clears field-specific validation errors.
   * Clear error for this field when user starts typing.
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>} e - Change event from a form control.
   * @returns {void}
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * Validates current formData and returns an errors map.
   * @returns {Record<string, string>} A map of field names to error messages. Empty if valid.
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.groupName.trim()) {
      newErrors.groupName = 'Group name is required';
    } else if (formData.groupName.length > 100) {
      newErrors.groupName = 'Group name must be less than 100 characters';
    }

    if (formData.groupDescription && formData.groupDescription.length > 500) {
      newErrors.groupDescription = 'Description must be less than 500 characters';
    }

    if (!formData.organizationId) {
      newErrors.organizationId = 'Organization is required';
    }

    return newErrors;
  };

  /**
   * Handles form submission. Runs validation. Calls API to create the security group.
   * Navigates to the groups list on success. Sets submit-level error message on failure.
   * @param {React.FormEvent<HTMLFormElement>} e - Form submit event
   * @returns {Promise<void>}
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      await addSecurityGroup({
        group_name: formData.groupName,
        group_description: formData.groupDescription || null,
        organization_id: parseInt(formData.organizationId)
      }).unwrap();

      navigate('/security-groups');
    } catch (error) {
      console.error('Failed to create security group:', error);
      setErrors({ submit: 'Failed to create security group. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Navigates back to the security groups list without saving changes.
   * @returns {void}
   */
  const handleCancel = () => navigate('/security-groups');

  return (
    <div className="page">
      <div className="page-inner">
        <header className="page-title-bar">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/security-groups">
                  <i className="breadcrumb-icon fa fa-angle-left mr-2"></i>
                  Security Groups
                </a>
              </li>
            </ol>
          </nav>
          <div className="d-flex justify-content-between">
            <h1 className="page-title">New Security Group</h1>
          </div>
        </header>

        <div className="page-section">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Group Information</h5>
            </Card.Header>
            <Card.Body>
              <div className="col-md-6 mb-3">
                <p className="text-muted small">
                  After creating the security group, you can assign users to this group, grant the group access to specific reports, and manage group permissions.
                </p>
              </div>

              {errors.submit && (
                <Alert variant="danger" className="mb-3">
                  {errors.submit}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Group Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="groupName"
                    value={formData.groupName}
                    onChange={handleInputChange}
                    isInvalid={!!errors.groupName}
                    placeholder="Enter group name (e.g., Finance Team, HR Department)"
                    maxLength={100}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.groupName}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="groupDescription"
                    value={formData.groupDescription}
                    onChange={handleInputChange}
                    isInvalid={!!errors.groupDescription}
                    placeholder="Enter group description (optional)"
                    maxLength={500}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.groupDescription}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    {formData.groupDescription.length}/500 characters
                  </Form.Text>
                </Form.Group>

                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Organization *</Form.Label>
                  <select
                    name="organizationId"
                    value={formData.organizationId}
                    onChange={handleInputChange}
                    className={`custom-select ${errors.organizationId ? 'is-invalid' : ''}`}
                  >
                    <option value="">
                      {orgLoading ? 'Loading organizations...' :
                       orgError ? 'Error loading organizations' :
                       'Select an organization'}
                    </option>
                    {organizations?.organizations?.map((org) => (
                      <option key={org.organization_id} value={org.organization_id}>
                        {org.organization_name}
                      </option>
                    ))}
                  </select>
                  {errors.organizationId && (
                    <div className="invalid-feedback">
                      {errors.organizationId}
                    </div>
                  )}
                </Form.Group>

                <div className="col-md-6 mb-3">
                  <div className="alert alert-info small">
                    <strong>Note:</strong> Users in this group will automatically inherit access to any reports assigned to the group.
                  </div>
                </div>

                <hr className="mb-0 mt-0"></hr>
                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating...' : 'Create Group'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ marginLeft: 10 }}
                    type="button"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </Form>
              </Card.Body>
            </Card>
          </div>
      </div>
    </div>
  );
};

export default CreateSecurityGroup;
